import React, { useLayoutEffect, useRef, useMemo } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

const PRODUCTS = ["HBB", "Baqati", "Hayyak", "Upgrade"];

const TargetPerPlanChart = ({ premiumData, standardData }) => {
  const chartRef = useRef(null);

  // 🔥 Transform EXACTLY your API structure
  const chartData = useMemo(() => {
    if (!premiumData && !standardData) return [];

    return PRODUCTS.map((product) => ({
      product,
      premiumTarget: premiumData?.[product]?.target ?? 0,
      premiumSales: premiumData?.[product]?.sales_count ?? 0,
      standardTarget: standardData?.[product]?.target ?? 0,
      standardSales: standardData?.[product]?.sales_count ?? 0,
    }));
  }, [premiumData, standardData]);

  useLayoutEffect(() => {
    if (!chartData.length) return;

    const chart = am4core.create("targetPerPlanChartDiv", am4charts.XYChart);
    chartRef.current = chart;
    chart.data = chartData;

    // X Axis
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "product";
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;

    // Y Axis
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.extraMax = 0.15;
    valueAxis.numberFormatter.numberFormat = "#,###";

    // ───────────── SALES (Columns) ─────────────
    const createColumnSeries = (field, name, color) => {
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = field;
      series.dataFields.categoryX = "product";
      series.name = name;

      series.columns.template.fill = am4core.color(color);
      series.columns.template.stroke = am4core.color(color);
      series.columns.template.width = am4core.percent(55);
      series.clustered = true;

      series.tooltipText = "{name}: {valueY.formatNumber('#,###')}";

      return series;
    };

    // ───────────── TARGET (Horizontal Line) ─────────────
    const createLineSeries = (field, name, color) => {
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = field;
      series.dataFields.categoryX = "product";
      series.name = name;

      series.stroke = am4core.color(color);
      series.strokeWidth = 3;
      series.tensionX = 1;
      series.fillOpacity = 0;
      series.zIndex = 10;

      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.radius = 4;
      bullet.circle.fill = am4core.color("#fff");
      bullet.circle.strokeWidth = 2;
      bullet.circle.stroke = am4core.color(color);

      series.tooltipText = "{name}: {valueY.formatNumber('#,###')}";

      return series;
    };

    // Premium
    createLineSeries("premiumTarget", "Premium Target", "#5B9BD5");
    createColumnSeries("premiumSales", "Premium Sales", "#7DB8E8");

    // Standard
    createLineSeries("standardTarget", "Standard Target", "#9B8EC4");
    createColumnSeries("standardSales", "Standard Sales", "#F4A460");

    chart.legend = new am4charts.Legend();
    chart.legend.position = "bottom";

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;
    chart.cursor.behavior = "none";

    return () => {
      chart.dispose();
    };
  }, [chartData]);

  if (!chartData.length) return null;

  return (
    <div style={{ width: "100%" }}>
      <div id="targetPerPlanChartDiv" style={{ width: "100%", height: 440 }} />
    </div>
  );
};

export default TargetPerPlanChart;
